import { ReactNode } from 'react';
import dayjs from 'dayjs';
import Lozenge from '@atlaskit/lozenge';
import SharedButton from 'shared/components/buttons/button';
import { useDialog } from 'core/providers/DialogProvider';
import { IDeliveryRequestDao } from 'core/api/types/delivery-requests.interface';
import { StatusLabels } from 'core/constants/delivery-request-status';

/**
 * Everything the requester told us, in one place.
 *
 * The list shows enough to decide at a glance; this is for when a glance is
 * not enough — an "Other" address, the equipment, the note at the bottom of the
 * form. Every answer is listed, blank ones included as a dash, so a missing
 * answer reads as not given rather than as not shown.
 *
 * The raw email stays out, as everywhere else on this screen: these are the
 * same answers, read.
 */

interface IProps {
  request: IDeliveryRequestDao;
}

const YES_NO = (value?: boolean) => (value === undefined ? undefined : value ? 'Yes' : 'No');

/** What the parser names the form's "other location (include postcode)" option. */
const OTHER_SITE_LABEL = 'Other Location';

const squashed = (value: string) => value.replace(/\s+/g, '').toUpperCase();

/**
 * A site, and where within it. The postcode is only ever given for "Other".
 *
 * "Other Location" is left off when there is an address to show instead: the
 * address already says it is not one of the named sites. Requesters usually
 * type the postcode into the address as well as into its own box, so it is left
 * off when the address already carries it, however spaced.
 */
export const describePlace = (site: string, location?: string, postcode?: string) => {
  const hasLocation = !!location?.trim();
  const repeated = !!postcode?.trim() && hasLocation && squashed(location!).includes(squashed(postcode!));
  const shownSite = site === OTHER_SITE_LABEL && hasLocation ? undefined : site;
  return [shownSite, location, repeated ? undefined : postcode].filter((part) => part && part.trim()).join(', ');
};

const Field = ({ label, value, testId }: { label: string; value?: ReactNode; testId?: string }) => (
  <div className='grid grid-cols-[180px_1fr] gap-3 py-1.5'>
    <dt className='text-gray-500'>{label}</dt>
    <dd data-testid={testId} className='whitespace-pre-wrap break-words'>
      {value === undefined || value === null || value === '' ? '—' : value}
    </dd>
  </div>
);

const Section = ({ title, children }: { title: string; children: ReactNode }) => (
  <section className='mt-4 first:mt-0'>
    <p className='body-02 mb-1 font-semibold'>{title}</p>
    <dl className='body-01 divide-y'>{children}</dl>
  </section>
);

const RequestDetailsDialog = ({ request: record }: IProps) => {
  const dialog = useDialog();
  const request = record.request;

  return (
    <div className='flex w-full flex-col rounded-lg bg-white lg:w-[680px]' data-testid='request-details-dialog'>
      <div className='flex items-center justify-between gap-3 border-b p-4'>
        <p className='headline-05 font-semibold'>{request?.requestType ?? 'Delivery request'}</p>
        <Lozenge>{StatusLabels[record.status] ?? record.status}</Lozenge>
      </div>

      <div className='max-h-[65vh] overflow-y-auto p-4'>
        <Section title='Request'>
          <Field label='Reference' value={record.reference ?? record.source.reference} testId='details-reference' />
          <Field
            label='Arrived'
            value={record.created?.at ? dayjs(record.created.at.toDate()).format('DD/MM/YYYY HH:mm') : undefined}
          />
          <Field label='Request type' value={request?.requestType} />
          <Field label='Reason' value={request?.reasonForRequest} />
          <Field label='Urgent' value={YES_NO(request?.urgent)} />
          <Field label='Pick-up required' value={request && `${request.requiredDate} ${request.requiredTime}`} />
        </Section>

        <Section title='Requester'>
          <Field label='Name' value={request?.requesterName} />
          <Field label='Email' value={request?.requesterEmail} />
          <Field label='Contact number' value={request?.contactNumber} />
          <Field label='Department' value={request?.department} />
          <Field label='Cost centre' value={request?.costCentre} />
          <Field label="Manager's email" value={request?.managerEmail} />
          {request?.respondentName && request.respondentName !== request.requesterName && (
            <Field label='Submitted by' value={request.respondentName} />
          )}
        </Section>

        <Section title='Collection'>
          <Field label='Site' value={request?.collectionSite} />
          <Field label='Location' value={request?.collectionLocation} testId='details-collection-location' />
          <Field label='Postcode' value={request?.collectionPostcode} testId='details-collection-postcode' />
        </Section>

        <Section title='Destination'>
          <Field label='Site' value={request?.destinationSite} />
          <Field label='Location' value={request?.destinationLocation} testId='details-destination-location' />
          <Field label='Postcode' value={request?.destinationPostcode} testId='details-destination-postcode' />
        </Section>

        <Section title='Requirements'>
          <Field label='Wheelchair required' value={YES_NO(request?.wheelchairRequired)} />
          <Field label='Wheelchair-accessible vehicle' value={YES_NO(request?.wheelchairAccessVehicleRequired)} />
          <Field label='Equipment' value={request?.equipmentSpecification} />
          <Field label='Additional information' value={request?.additionalInformation} testId='details-additional' />
        </Section>
      </div>

      <div className='flex justify-end gap-2 border-t p-4'>
        <SharedButton type='button' appearance='subtle' label='Close' onClick={() => dialog?.closeDialog()} />
      </div>
    </div>
  );
};

export default RequestDetailsDialog;
